import { useState, type FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface SignUpFormProps {
  readonly onNeedConfirmation: (email: string) => void;
  readonly onSignInClick: () => void;
}

const inputStyle = {
  marginTop: 'var(--space-1)',
  display: 'block',
  width: '100%',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-default)',
  padding: 'var(--space-2) var(--space-3)',
  minHeight: '44px',
  fontSize: 'var(--text-base)',
  backgroundColor: 'var(--bg-surface)',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color var(--duration-fast) var(--ease-out)',
} as const;

const labelStyle = {
  display: 'block',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text-secondary)',
} as const;

export const SignUpForm = ({ onNeedConfirmation, onSignInClick }: SignUpFormProps) => {
  const { signUp } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      await signUp(email, password, name);
      onNeedConfirmation(email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'var(--border-focus)';
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = 'var(--border-default)';
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        Create Account
      </h2>

      {error && (
        <div
          role="alert"
          style={{
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-error-50)',
            padding: 'var(--space-3)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-error-700)',
          }}
        >
          {error}
        </div>
      )}

      <div>
        <label htmlFor="name" style={labelStyle}>Full Name</label>
        <input id="name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="focus-ring" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
      </div>

      <div>
        <label htmlFor="signup-email" style={labelStyle}>Email</label>
        <input id="signup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="focus-ring" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
      </div>

      <div>
        <label htmlFor="signup-password" style={labelStyle}>Password</label>
        <input id="signup-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="focus-ring" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
      </div>

      <div>
        <label htmlFor="confirm-password" style={labelStyle}>Confirm Password</label>
        <input id="confirm-password" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="focus-ring" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="focus-ring"
        style={{
          width: '100%',
          borderRadius: 'var(--radius-md)',
          backgroundColor: 'var(--color-primary-600)',
          padding: 'var(--space-3) var(--space-4)',
          minHeight: '44px',
          color: 'var(--text-inverse)',
          fontWeight: 'var(--font-weight-medium)',
          border: 'none',
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          opacity: isSubmitting ? 0.5 : 1,
          transition: 'background-color var(--duration-fast) var(--ease-out)',
        }}
        onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'; }}
      >
        {isSubmitting ? 'Creating Account...' : 'Create Account'}
      </button>

      <p className="text-center" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSignInClick}
          style={{
            color: 'var(--text-accent)',
            fontWeight: 'var(--font-weight-medium)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Sign In
        </button>
      </p>
    </form>
  );
};
